import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  getDocs,
  serverTimestamp,
  setDoc,
  doc,
  onSnapshot,
  collection,
  addDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "../firebaseConfig";
import TablaRemisiones from "./TablaRemisiones";
import "react-datepicker/dist/react-datepicker.css";
import Nav from "./Nav";
import { useNavContext } from "../context/NavContext";
import Sidebar from "./Sidebar";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import useProductos from "../hooks/useProductos";
import { flushSync } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import ModalRemision from "./ModalRemision";
import SelectorCotizacionModal from "./SelectorCotizacionModal";

function RemisionForm(props) {
  const { remision, closeModal, setRemisiones, cloneMode = false } = props;
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const [parentId] = useState(remision?.id ?? uuidv4());
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [observacion, setObservacion] = useState("");
  const [condicion, setCondicion] = useState("");
  const [vendedorNombre, setVendedorNombre] = useState("");
  const [vendedorCorreo, setVendedorCorreo] = useState("");
  const [referencia, setReferencia] = useState("");
  const [asunto, setAsunto] = useState("");
  const [mostrarTabla] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [numeroCotizacion, setNumeroCotizacion] = useState("");
  const [cotizacionesRelacionadas, setCotizacionesRelacionadas] = useState([]);
  const [fechaRemision, setFechaRemision] = useState(new Date());
  const [catalogo, setCatalogo] = useState([]);
  const [atencion, setAtencion] = useState("");
  const [planta, setPlanta] = useState("");
  const [area, setArea] = useState("");
  const [notas, setNotas] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [terminos, setTerminos] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [selectorCotiOpen, setSelectorCotiOpen] = useState(false);
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    filteredProductos,
    iva,
    total,
    manejarCambioDescuento,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    setDescuento,
    descMode,
    toggleDescMode,
    totalDescuentoGlobal,
    moveProducto,
  } = useProductos(
    remision?.productosSeleccionados || [],
    remision?.descuento || 0,
    catalogo
  );
  const {
    numero: numeroRemisionAuto,
    modo: modoNumeroRemision,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroRemision", {
    title: "Configurar número de remisión",
    ownerUid,
    onManualNumeroChange: setNumeroSobrescrito,
  });

  useEffect(() => {
    if (remision && !cloneMode) {
      // ── MODO EDICIÓN ─────────────────────────────
      setModoEdicion(true);

      // Campos básicos
      setNumeroSobrescrito(remision.numeroRemision ?? null);
      setCliente(remision.cliente);
      setNumeroCotizacion(remision.numeroCotizacion);
      setCotizacionesRelacionadas(remision.cotizacionesRelacionadas || []);
      setFechaRemision(
        remision.fechaRemision ? new Date(remision.fechaRemision) : new Date()
      );
      setCondicion(remision.condicion);
      setAtencion(remision.atencion);
      setReferencia(remision.referencia);
      setObservacion(remision.observacion);
      setDivisa(remision.divisa);
      setPlanta(remision.planta);
      setArea(remision.area);
      setVendedorNombre(remision.vendedorNombre ?? "");
      setVendedorCorreo(remision.vendedorCorreo ?? "");
      setIncoterm(remision.incoterm ?? "");
      setAsunto(remision.asunto);

      // Productos y descuento
      setDatos(
        (remision.productosSeleccionados ?? []).map((p) => ({
          id: uuidv4(),
          ...p,
          // compatibilidad: si solo existe numeroDeParteCliente lo usamos
          numeroDeParteProveedor:
            p.numeroDeParteProveedor ?? p.numeroDeParteCliente ?? "",
        }))
      );
      setDescuento(parseFloat(remision.descuento) || 0);

      // Notas y términos
      setNotas(remision.notas);
      setTerminos(remision.terminos);

      // Los archivos los gestiona automáticamente useArchivos()
    } else if (remision && cloneMode) {
      // ── MODO CLONACIÓN ────────────────────────────
      setModoEdicion(false);

      // Precargamos todos los campos igual que en edición,
      // pero tratamos como nueva creación (no actualizamos createdAt ni contador)
      setNumeroSobrescrito(remision.numeroRemision);
      setCliente(remision.cliente);
      setNumeroCotizacion(remision.numeroCotizacion);
      setCotizacionesRelacionadas(remision.cotizacionesRelacionadas || []);
      setFechaRemision(
        remision.fechaRemision ? new Date(remision.fechaRemision) : new Date()
      );
      setCondicion(remision.condicion);
      setAtencion(remision.atencion);
      setReferencia(remision.referencia);
      setObservacion(remision.observacion);
      setDivisa(remision.divisa);
      setPlanta(remision.planta);
      setArea(remision.area);
      setVendedorNombre(remision.vendedorNombre ?? "");
      setVendedorCorreo(remision.vendedorCorreo ?? "");
      setIncoterm(remision.incoterm ?? "");
      setAsunto(remision.asunto);

      setDatos(
        (remision.productosSeleccionados ?? []).map((p) => ({
          id: uuidv4(),
          ...p,
          numeroDeParteProveedor:
            p.numeroDeParteProveedor ?? p.numeroDeParteCliente ?? "",
        }))
      );
      setDescuento(parseFloat(remision.descuento) || 0);

      setNotas(remision.notas);
      setTerminos(remision.terminos);
    } else {
      // ── MODO CREACIÓN NUEVA ───────────────────────
      setModoEdicion(false);
      setNumeroSobrescrito(null);
      setNumeroCotizacion("");
      setCotizacionesRelacionadas([]);

      // Inicializamos una sola fila vacía
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          descripcion: "",
          numeroDeParteProveedor: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);

      // (los archivos se suscriben con parentId automáticamente)
    }
  }, [remision, cloneMode, setDatos, setDescuento]);

  useEffect(() => {
    if (!ownerUid) return;
    const pairs = [
      ["clientes", setClientes],
      ["productosClientes", setCatalogo],
    ];
    const unsubs = pairs.map(([col, setter]) =>
      onSnapshot(getSubCollection(ownerUid, col), (snap) =>
        setter(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [ownerUid]);

  const handleRowDragStart = (index) => {
    setDragIndex(index);
  };

  const handleRowDragOver = (e, index) => {
    e.preventDefault();
    setOverIndex(index);
  };

  const handleRowDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }

    moveProducto(dragIndex, index);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleRowDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const buildHistorialCambios = (remisionOriginal, nuevaRemision) => {
    const cambios = [];

    // Helper para registrar un cambio con ambos nombres de campo
    const addCambio = ({ campo, descripcion, anterior, nuevo, extra = {} }) => {
      if (anterior === nuevo) return;

      const anteriorVal = anterior ?? "";
      const nuevoVal = nuevo ?? "";

      cambios.push({
        tipo: "cambio",
        campo,
        descripcion,
        // nombres que usa ModalHistorial
        anterior: anteriorVal,
        nuevo: nuevoVal,
        // nombres "viejos" que ya tenías y que hacían que se vea bien
        valorAnterior: anteriorVal,
        valorNuevo: nuevoVal,
        ...extra,
      });
    };

    // 🟢 CREACIÓN
    if (!remisionOriginal) {
      addCambio({
        campo: "remision",
        descripcion: `creó la remisión ${nuevaRemision.numeroRemision}`,
        anterior: null,
        nuevo: nuevaRemision.numeroRemision,
      });
      return cambios;
    }

    // Helper para campos simples tipo texto/fecha/divisa/etc.
    const pushCambio = (campo, label, anterior, nuevo) => {
      addCambio({
        campo,
        descripcion: `cambió ${label} de la remisión ${nuevaRemision.numeroRemision}`,
        anterior,
        nuevo,
      });
    };

    // 0) Número de remisión
    if (remisionOriginal.numeroRemision !== nuevaRemision.numeroRemision) {
      addCambio({
        campo: "numeroRemision",
        descripcion: "cambió el número de remisión",
        anterior: remisionOriginal.numeroRemision || "",
        nuevo: nuevaRemision.numeroRemision || "",
      });
    }

    // 1) Fecha de remisión
    pushCambio(
      "fechaRemision",
      "la fecha",
      remisionOriginal.fechaRemision,
      nuevaRemision.fechaRemision
    );

    // 2) Cliente
    pushCambio(
      "cliente",
      "el cliente",
      remisionOriginal.nombreCliente || remisionOriginal.cliente,
      nuevaRemision.nombreCliente || nuevaRemision.cliente
    );

    // 3) Número de cotización
    pushCambio(
      "numeroCotizacion",
      "el número de cotización",
      remisionOriginal.numeroCotizacion,
      nuevaRemision.numeroCotizacion
    );

    // 4) Divisa
    pushCambio(
      "divisa",
      "la moneda",
      remisionOriginal.divisa,
      nuevaRemision.divisa
    );

    // 5) Tiempo de entrega (condicion)
    pushCambio(
      "condicion",
      "el tiempo de entrega",
      remisionOriginal.condicion,
      nuevaRemision.condicion
    );

    // 6) Condiciones de pago (observacion)
    pushCambio(
      "observacion",
      "las condiciones de pago",
      remisionOriginal.observacion,
      nuevaRemision.observacion
    );

    // 7) Notas
    pushCambio(
      "notas",
      "las notas",
      remisionOriginal.notas,
      nuevaRemision.notas
    );

    // 8) Términos
    pushCambio(
      "terminos",
      "los términos y condiciones",
      remisionOriginal.terminos,
      nuevaRemision.terminos
    );

    // 9) Incoterm
    pushCambio(
      "incoterm",
      "el incoterm",
      remisionOriginal.incoterm,
      nuevaRemision.incoterm
    );

    // 10) Área / Planta / Atención / Referencia
    pushCambio(
      "planta",
      "la planta",
      remisionOriginal.planta,
      nuevaRemision.planta
    );
    pushCambio("area", "el área", remisionOriginal.area, nuevaRemision.area);
    pushCambio(
      "atencion",
      "la atención",
      remisionOriginal.atencion,
      nuevaRemision.atencion
    );
    pushCambio(
      "referencia",
      "la referencia",
      remisionOriginal.referencia,
      nuevaRemision.referencia
    );

    // 11) Descuento global
    pushCambio(
      "descuento",
      "el descuento global",
      remisionOriginal.descuento,
      nuevaRemision.descuento
    );

    // 12) TOTAL global de la remisión
    pushCambio(
      "total",
      "el total",
      remisionOriginal.total,
      nuevaRemision.total
    );

    // ========= CAMBIOS EN PRODUCTOS =========
    const oldList = remisionOriginal.productosSeleccionados || [];
    const newList = nuevaRemision.productosSeleccionados || [];

    const calcularSubtotalLinea = (p) => {
      const tarifa = Number(p.tarifa) || 0;
      const cantidad = Number(p.cantidad) || 0;
      const base = tarifa * cantidad;

      const d = Number(p.descuento) || 0;
      const tipo = p.tipoDescuento || "%";

      let descLinea = tipo === "%" ? base * (d / 100) : d;
      if (descLinea > base) descLinea = base;

      return base - descLinea;
    };

    const divisa = nuevaRemision.divisa || "";
    const formatMoney = (num) =>
      `${divisa ? divisa + " " : ""}${Number(num || 0).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    newList.forEach((pNew) => {
      const key =
        pNew.modelo ||
        pNew.numeroDeParteProveedor ||
        pNew.numeroDeParteCliente ||
        pNew.nombre;

      if (!key) return;

      const pOld =
        oldList.find(
          (p) =>
            (p.modelo ||
              p.numeroDeParteProveedor ||
              p.numeroDeParteCliente ||
              p.nombre) === key
        ) || null;

      if (!pOld) return;

      const nombreProd = pNew.nombre || key;

      const oldPrice = Number(pOld.tarifa) || 0;
      const newPrice = Number(pNew.tarifa) || 0;
      const oldCant = Number(pOld.cantidad) || 0;
      const newCant = Number(pNew.cantidad) || 0;
      const oldDesc = Number(pOld.descuento) || 0;
      const newDesc = Number(pNew.descuento) || 0;
      const oldTipoDesc = pOld.tipoDescuento || "%";
      const newTipoDesc = pNew.tipoDescuento || "%";

      const oldSub = calcularSubtotalLinea(pOld);
      const newSub = calcularSubtotalLinea(pNew);

      // 12.a) Cambio de PRECIO
      if (oldPrice !== newPrice) {
        addCambio({
          campo: "precioProducto",
          descripcion: `modificó el precio del producto ${nombreProd} en la remisión ${nuevaRemision.numeroRemision}`,
          anterior: formatMoney(oldPrice),
          nuevo: formatMoney(newPrice),
          extra: { productoNombre: nombreProd },
        });
      }

      // 12.b) Cambio de CANTIDAD (piezas, sin símbolo de moneda)
      if (oldCant !== newCant) {
        const fmtCant = (val) =>
          `${Number(val || 0)} ${Number(val || 0) === 1 ? "pieza" : "piezas"}`;

        addCambio({
          campo: "cantidadProducto",
          descripcion: `cambió la cantidad del producto ${nombreProd} en la remisión ${nuevaRemision.numeroRemision}`,
          anterior: fmtCant(oldCant),
          nuevo: fmtCant(newCant),
          extra: { productoNombre: nombreProd },
        });
      }

      // 12.c) Cambio de DESCUENTO de la línea
      if (oldDesc !== newDesc || oldTipoDesc !== newTipoDesc) {
        const fmtDesc = (val, tipo) =>
          tipo === "%" ? `${val}%` : `${formatMoney(val)}`;

        addCambio({
          campo: "descuentoProducto",
          descripcion: `cambió el descuento del producto ${nombreProd} en la remisión ${nuevaRemision.numeroRemision}`,
          anterior: fmtDesc(oldDesc, oldTipoDesc),
          nuevo: fmtDesc(newDesc, newTipoDesc),
          extra: { productoNombre: nombreProd },
        });
      }

      // 12.d) Cambio de SUBTOTAL de la línea
      if (oldSub !== newSub) {
        addCambio({
          campo: "subtotalProducto",
          descripcion: `cambió el subtotal del producto ${nombreProd} en la remisión ${nuevaRemision.numeroRemision}`,
          anterior: formatMoney(oldSub),
          nuevo: formatMoney(newSub),
          extra: { productoNombre: nombreProd },
        });
      }
    });

    // 13) Si no se detectó nada específico, registramos uno genérico
    if (cambios.length === 0) {
      cambios.push({
        tipo: "cambio",
        campo: "remision",
        descripcion: `actualizó la remisión ${nuevaRemision.numeroRemision}`,
        anterior: null,
        nuevo: null,
        valorAnterior: null,
        valorNuevo: null,
      });
    }

    return cambios;
  };

  const guardar = async (e) => {
    e.preventDefault();

    // ---------- Helpers compactos ----------
    const nNum = (x, def = 0) => (Number.isFinite(x) ? x : def);
    const nStr = (x, def = "") => x ?? def;

    const normalizeProducto = (p) => {
      const cantidad = nNum(p?.cantidad, 1);
      const tarifa = nNum(p?.tarifa, 0);

      // unificamos el campo como en Cotización
      const numeroDeParteProveedor = nStr(
        p?.numeroDeParteProveedor ?? p?.numeroDeParteCliente
      );

      return {
        id: nStr(p?.id, uuidv4()),
        nombre: nStr(p?.nombre),
        descripcion: nStr(p?.descripcion ?? p?.nombre),
        modelo: nStr(p?.modelo ?? numeroDeParteProveedor),
        numeroDeParteProveedor,
        cantidad,
        tarifa,
        tipoDescuento: p?.tipoDescuento ?? "%",
        descuento: nNum(p?.descuento, 0),
        importe: Number.isFinite(p?.importe)
          ? Number(p?.importe)
          : tarifa * cantidad,
        iva: nNum(p?.iva, 0),
      };
    };

    const pruneUndefinedDeep = (val) => {
      if (Array.isArray(val)) return val.map(pruneUndefinedDeep);
      if (val && typeof val === "object") {
        const out = {};
        for (const [k, v] of Object.entries(val)) {
          if (v === undefined) continue;
          out[k] = pruneUndefinedDeep(v);
        }
        return out;
      }
      return val;
    };
    // ---------------------------------------

    /* 1) VALIDACIÓN */
    if (!cliente || !fechaRemision) {
      await Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Por favor completa los campos: Cliente y Fecha de la Remisión.",
      });
      return;
    }

    /* 2) Número de remisión efectivo */
    const numeroEfectivo =
      numeroSobrescrito != null && numeroSobrescrito !== ""
        ? numeroSobrescrito
        : numeroRemisionAuto;

    try {
      /* 3) DUPLICADOS */
      const remisionesCol = getSubCollection(ownerUid, "remisiones");
      const snap = await getDocs(remisionesCol);
      const dup = snap.docs.find(
        (d) => d.data().numeroRemision === numeroEfectivo
      );
      if (dup && (!modoEdicion || dup.id !== remision?.id)) {
        await Swal.fire({
          icon: "error",
          title: "Número de remisión duplicado",
          text: `El número "${numeroEfectivo}" ya existe. Usa otro.`,
        });
        return;
      }

      /* 4) PAYLOAD (normalizado + sin undefined) */
      const sel = clientes.find((c) => c.id === cliente) || {};
      const nombreCliente = sel.empresa || sel.nombre || "";

      const productosLimpios = (datos || []).map(normalizeProducto);

      const baseData = {
        cliente,
        nombreCliente,
        numeroRemision: numeroEfectivo,
        numeroCotizacion: nStr(numeroCotizacion),
        cotizacionesRelacionadas: (cotizacionesRelacionadas || []).map((c) => ({
          id: c.id,
          numeroCotizacion: c.numeroCotizacion || "",
          nombreCliente: c.nombreCliente || "",
        })),
        fechaRemision: fechaRemision.toISOString(),
        atencion: nStr(atencion),
        referencia: nStr(referencia),
        observacion: nStr(observacion),
        planta: nStr(planta),
        vendedorNombre: nStr(vendedorNombre),
        vendedorCorreo: nStr(vendedorCorreo),
        condicion: nStr(condicion),
        area: nStr(area),
        divisa: nStr(divisa),
        incoterm: nStr(incoterm),
        asunto: nStr(asunto),
        productosSeleccionados: productosLimpios,
        total: nNum(total, 0),
        notas: nStr(notas),
        terminos: nStr(terminos),
        descuento: nNum(descuento, 0),
        estado: "Pendiente",
        ...(modoEdicion ? {} : { createdAt: serverTimestamp() }),
      };

      const remisionData = pruneUndefinedDeep(baseData);

      // --- 4.1 Historial de cambios (comparando original vs nueva) ---
      const nuevaRemisionParaHistorial = {
        ...(remision || {}),
        ...remisionData,
        productosSeleccionados: productosLimpios,
      };

      // buildHistorialCambios debe estar definido arriba del componente
      const historialCambios = buildHistorialCambios(
        modoEdicion ? remision : null,
        nuevaRemisionParaHistorial
      );

      /* 5) CREACIÓN O EDICIÓN */
      if (modoEdicion && remision?.id) {
        const docRef = doc(remisionesCol, remision.id);
        await setDoc(docRef, remisionData, { merge: true });

        // --- Historial para EDICIÓN ---
        if (historialCambios.length > 0) {
          const histSubCol = collection(
            db,
            "usuarios",
            ownerUid,
            "remisiones",
            remision.id,
            "historial"
          );

          await Promise.all(
            historialCambios.map((c) =>
              addDoc(histSubCol, {
                ...c,
                usuarioId: user.uid,
                usuarioNombre: user.displayName || user.email,
                createdAt: serverTimestamp(),
              })
            )
          );
        }

        // Refrescar lista en memoria si el padre nos pasó el setter
        setRemisiones?.((prev) =>
          prev.map((r) =>
            r.id === remision.id ? { id: remision.id, ...remisionData } : r
          )
        );

        await Swal.fire({
          icon: "success",
          title: "Remisión actualizada",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        const docRef = doc(remisionesCol, parentId);
        await setDoc(docRef, remisionData);

        // --- Historial para CREACIÓN ---
        if (historialCambios.length > 0) {
          const histSubCol = collection(
            db,
            "usuarios",
            ownerUid,
            "remisiones",
            parentId,
            "historial"
          );

          await Promise.all(
            historialCambios.map((c) =>
              addDoc(histSubCol, {
                ...c,
                usuarioId: user.uid,
                usuarioNombre: user.displayName || user.email,
                createdAt: serverTimestamp(),
              })
            )
          );
        }

        // Autoincremento si está en automático
        if (modoNumeroRemision === "auto") {
          // Soporta prefijo opcional (sin necesidad de guion) y dígitos al final
          // Ejemplos válidos: "RE001", "REM-001", "0005"
          const m = String(numeroEfectivo).match(/^(\D*?)(\d+)$/);

          if (m) {
            const [, prefijo = "", raw] = m; // prefijo = "RE", raw = "001"
            const siguienteNumero = parseInt(raw, 10) + 1;

            const configRef = doc(
              db,
              "usuarios",
              ownerUid,
              "configuracion",
              "numeroRemision"
            );

            await setDoc(
              configRef,
              { modo: "auto", prefijo, siguienteNumero },
              { merge: true }
            );
          }
        }

        setNumeroSobrescrito(null);

        await Swal.fire({
          icon: "success",
          title: "Remisión guardada",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      /* 6) LIMPIEZA Y CIERRE */
      flushSync(() => setFormDirty(false));
      closeModal();
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: err.message,
      });
    }
  };

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
    }
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <ModalRemision
        numeroRemisionAuto={numeroRemisionAuto}
        numeroSobrescrito={numeroSobrescrito}
        modoEdicion={modoEdicion}
        abrirConfiguracion={abrirConfiguracion}
        onChangeNumeroRemision={setNumeroSobrescrito}
        modoNumero={modoNumeroRemision}
        /* --- cliente / datos generales --- */
        clientes={clientes}
        cliente={cliente}
        setCliente={setCliente}
        divisa={divisa}
        setDivisa={setDivisa}
        /* --- fechas --- */
        fechaCotizacion={fechaRemision}
        setFechaCotizacion={setFechaRemision}
        /* --- NUEVO: campo Cotización --- */
        numeroCotizacion={numeroCotizacion}
        onOpenSelectorCotizacion={() => setSelectorCotiOpen(true)}
        /* --- productos, totales, etc... (lo que ya tenías) --- */
        datos={datos}
        setDatos={setDatos}
        handleInputChange={handleInputChange}
        filteredProductos={filteredProductos}
        setFilteredProductos={setFilteredProductos}
        actualizarCantidad={actualizarCantidad}
        eliminar={eliminar}
        añadirFila={añadirFila}
        catalogo={catalogo}
        calcularSubtotal={calcularSubtotal}
        descuento={descuento}
        manejarCambioDescuento={manejarCambioDescuento}
        iva={iva}
        total={total}
        descMode={descMode}
        toggleDescMode={toggleDescMode}
        totalDescuentoGlobal={totalDescuentoGlobal}
        tiempoEntrega={condicion}
        setTiempoEntrega={setCondicion}
        condicionesPago={observacion}
        setCondicionesPago={setObservacion}
        notas={notas}
        setNotas={setNotas}
        overIndex={overIndex}
        onRowDragStart={handleRowDragStart}
        onRowDragOver={handleRowDragOver}
        onRowDrop={handleRowDrop}
        onRowDragEnd={handleRowDragEnd}
        onGuardar={guardar}
        onCancelar={handleCancel}
      />

      {selectorCotiOpen && (
        <SelectorCotizacionModal
          ownerUid={ownerUid}
          onClose={() => setSelectorCotiOpen(false)}
          // 👇 marcamos las que ya están relacionadas
          preselectedIds={cotizacionesRelacionadas.map((c) => c.id)}
          onSelect={(seleccionadas) => {
            // guarda TODAS para el modal
            setCotizacionesRelacionadas(
              seleccionadas.map((c) => ({
                id: c.id,
                numeroCotizacion: c.numeroCotizacion || "",
                nombreCliente: c.nombreCliente || "",
              }))
            );

            // 👇 pero en la ficha de la remisión solo se ve la primera (como IC-0009)
            const primera = seleccionadas[0];
            setNumeroCotizacion(primera?.numeroCotizacion || "");

            setSelectorCotiOpen(false);
          }}
        />
      )}

      {mostrarTabla && <TablaRemisiones remisiones={props.remisiones} />}

      <NumeroConfigModalUI />
    </>
  );
}

export default RemisionForm;
